import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Voice Control Hook for Hayat Family Device
 * 
 * Features:
 * - Push-to-Talk mode (no continuous listening)
 * - Web Speech API integration
 * - Command recognition for navigation
 * - Stop command for TTS/Radio
 * - German language support ("de-DE")
 * 
 * Usage:
 * const {
 *   isListening,
 *   startListening,
 *   stopListening,
 *   transcript,
 *   error,
 *   isSupported
 * } = useVoiceControl({ onCommand });
 */
export function useVoiceControl({ onCommand }) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(true);
  
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);

  // Check browser support
  useEffect(() => {
    console.log('[VoiceControl] Initializing...');
    
    // Check for Speech Recognition API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn('[VoiceControl] Speech Recognition API not supported in this browser');
      setIsSupported(false);
      return;
    }

    // Detect iOS/iPadOS devices (Speech Recognition doesn't work reliably there)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isIPadOS = navigator.maxTouchPoints > 1 && /MacIntel/.test(navigator.platform);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // Only block iOS/iPadOS Safari (not desktop Safari)
    if ((isIOS || isIPadOS) && isSafari) {
      console.warn('[VoiceControl] Speech Recognition not available on iOS/iPadOS Safari');
      setIsSupported(false);
      return;
    }

    // Initialize Speech Recognition
    try {
      console.log('[VoiceControl] Creating SpeechRecognition instance...');
      const recognition = new SpeechRecognition();
      console.log('[VoiceControl] SpeechRecognition created successfully');
    recognition.lang = 'de-DE';
    recognition.continuous = false; // Stop after one result
    recognition.interimResults = false; // Only final results
    recognition.maxAlternatives = 1;

    // Handle results
    recognition.onresult = (event) => {
      const result = event.results[0][0].transcript;
      const confidence = event.results[0][0].confidence;
      
      console.log('[VoiceControl] Result:', result, 'Confidence:', confidence);
      setTranscript(result);
      
      // Process command
      if (onCommand) {
        processCommand(result);
      }
    };

    // Handle errors
    recognition.onerror = (event) => {
      // Suppress 'aborted' errors (common on iOS/Safari, also user cancellations)
      if (event.error === 'aborted') {
        console.info('[VoiceControl] Recognition aborted (user cancelled or browser limitation)');
        setIsListening(false);
        return;
      }
      
      console.error('[VoiceControl] Error:', event.error);
      
      let errorMessage = 'Ein Fehler ist aufgetreten.';
      
      switch (event.error) {
        case 'no-speech':
          errorMessage = 'Ich habe nichts gehört. Bitte versuchen Sie es erneut.';
          break;
        case 'audio-capture':
          errorMessage = 'Mikrofon nicht verfügbar. Bitte überprüfen Sie die Berechtigungen.';
          break;
        case 'not-allowed':
          errorMessage = 'Mikrofon-Zugriff verweigert. Bitte aktivieren Sie das Mikrofon.';
          break;
        case 'network':
          errorMessage = 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
          break;
        default:
          errorMessage = `Fehler: ${event.error}`;
      }
      
      setError(errorMessage);
      setIsListening(false);
    };

    // Handle end
    recognition.onend = () => {
      console.log('[VoiceControl] Recognition ended');
      setIsListening(false);
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    // Handle start
    recognition.onstart = () => {
      console.log('[VoiceControl] Recognition started');
      setIsListening(true);
      setError(null);
      setTranscript('');
      
      // Set 10 second timeout
      timeoutRef.current = setTimeout(() => {
        console.log('[VoiceControl] Timeout reached');
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
        setError('Zeitüberschreitung. Bitte versuchen Sie es erneut.');
      }, 10000);
    };

        recognitionRef.current = recognition;
      setIsSupported(true);
      console.log('[VoiceControl] Initialization complete, isSupported=true');

      return () => {
        try {
          if (recognitionRef.current) {
            recognitionRef.current.abort();
          }
        } catch (e) {
          // Silently handle cleanup errors
          console.warn('[VoiceControl] Cleanup error:', e.message);
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    } catch (e) {
      console.error('[VoiceControl] Failed to initialize Speech Recognition:', e);
      setIsSupported(false);
      return;
    }
  }, [onCommand]);

  // Process voice command
  const processCommand = useCallback((text) => {
    const command = text.toLowerCase().trim();
    
    console.log('[VoiceControl] Processing command:', command);
    
    // Command mapping
    const commandMap = {
      // Weather
      'wetter': 'weather',
      
      // News
      'nachrichten': 'news',
      
      // Music
      'musik': 'music',
      
      // Stories
      'geschichten': 'stories',
      
      // Prayer Times
      'gebetszeiten': 'prayer_times',
      'gebet': 'prayer_times',
      
      // Emergency
      'notfall': 'emergency',
      'hilfe': 'emergency',
      
      // Call Family
      'familie anrufen': 'call',
      'anrufen': 'call',
      'familie': 'call',
      'anruf': 'call',
      
      // Stop
      'stop': 'stop',
      'stopp': 'stop',
      'halt': 'stop',
      'pause': 'stop',
      
      // Back/Home
      'zurück': 'home',
      'startseite': 'home',
      'home': 'home',
      'anfang': 'home'
    };
    
    // Find matching command
    let matchedCommand = null;
    
    for (const [keyword, action] of Object.entries(commandMap)) {
      if (command.includes(keyword)) {
        matchedCommand = action;
        break;
      }
    }
    
    if (matchedCommand) {
      console.log('[VoiceControl] Matched command:', matchedCommand);
      if (onCommand) {
        onCommand(matchedCommand);
      }
    } else {
      console.log('[VoiceControl] No command matched');
      setError('Ich habe das nicht verstanden. Bitte versuchen Sie es erneut.');
    }
  }, [onCommand]);

  // Start listening
  const startListening = useCallback(() => {
    console.log('[VoiceControl] startListening called', {
      isSupported,
      isListening,
      hasRecognition: !!recognitionRef.current
    });
    
    if (!isSupported) {
      console.warn('[VoiceControl] Cannot start - not supported');
      return;
    }
    
    if (isListening) {
      console.warn('[VoiceControl] Already listening');
      return;
    }
    
    if (!recognitionRef.current) {
      console.error('[VoiceControl] No recognition instance available');
      return;
    }
    
    try {
      console.log('[VoiceControl] Starting recognition...');
      setError(null);
      setTranscript('');
      recognitionRef.current.start();
    } catch (err) {
      console.error('[VoiceControl] Start error:', err);
      setError('Fehler beim Starten der Spracherkennung.');
    }
  }, [isSupported, isListening]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (!isListening) {
      return;
    }
    
    console.log('[VoiceControl] Stopping...');
    recognitionRef.current?.stop();
  }, [isListening]);

  // Toggle listening
  const toggleListening = useCallback(() => {
    console.log('[VoiceControl] toggleListening called', { isListening });
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isListening,
    startListening,
    stopListening,
    toggleListening,
    transcript,
    error,
    isSupported
  };
}
